/**
 * Pure mutation functions for simulation-related actions.
 */

import type {IControlConfig} from "@buildwithflux/core";
import {documentConfigUid, documentConfigWithIds} from "@buildwithflux/core";
import type {
    ControlPositionData,
    IDocumentConfigDataWithoutIds,
    IDocumentData,
    IUserData,
    SimulationTimeStepSizeUnits,
} from "@buildwithflux/models";

/**
 * Pure mutation function for updating the simulation time step size.
 */
export function applyUpdateSimulationTimeStep(draft: IDocumentData, value: number): void {
    draft.simulation.time_step_size = value;
}

/**
 * Pure mutation function for updating the simulation time step unit.
 */
export function applyUpdateSimulationTimeStepUnit(draft: IDocumentData, unit: SimulationTimeStepSizeUnits): void {
    draft.simulation.time_step_size_unit = unit;
}

/**
 * Pure mutation function for setting simulation controls.
 */
export function applySetControls(draft: IDocumentData, controls: IControlConfig[]): void {
    draft.controls = controls;
}

/**
 * Pure mutation function for setting simulation code (behaviour).
 */
export function applySetCode(draft: IDocumentData, code: string, hasErrors: boolean): void {
    draft.behaviour = code;
    draft.behaviour_has_errors = hasErrors;
}

/**
 * Pure mutation function for setting document configs.
 */
export function applySetDocumentConfigs(
    draft: IDocumentData,
    configs: IDocumentConfigDataWithoutIds[],
    currentUserUid: string,
): void {
    const oldConfigs = draft.configs;

    for (const configData of configs) {
        if (configData.key && draft) {
            const uid = documentConfigUid({
                key: configData.key,
                section: configData.section,
                user_uid: currentUserUid,
            });
            const newConfig = documentConfigWithIds(configData, currentUserUid);

            if (!oldConfigs[uid]) {
                oldConfigs[uid] = newConfig;
            } else {
                oldConfigs[uid] = {...oldConfigs[uid], ...newConfig};
            }
        }
    }
}

/**
 * Pure mutation function for setting simulation control values for an element.
 */
export function applySetSimulationControlsValue(
    draft: IDocumentData,
    elementUid: string,
    controlUid: string,
    value: ControlPositionData,
    currentUserUid: string,
): void {
    const section = "control_values";
    const key = `simulation_controls_${elementUid}`;
    const uid = documentConfigUid({key, section, user_uid: currentUserUid});

    const targetConfig = draft.configs[uid];
    if (targetConfig) {
        const existingValue =
            typeof targetConfig.value === "object" && targetConfig.value !== null ? targetConfig.value : {};
        targetConfig.value = {...existingValue, [controlUid]: value};
    } else {
        draft.configs[uid] = {
            uid,
            user_uid: currentUserUid,
            section,
            key,
            value: {[controlUid]: value},
        };
    }
}
